/**
 * @fileoverview Reusable confirmation modal for a single potentially destructive action.
 * @module ui-admin/components/ui/ConfirmActionModal
 *
 * Responsibilities:
 * - Present an action summary, optional warning, and confirm/cancel controls.
 * - Prevent duplicate confirmation while the host reports a submission in progress.
 * Dependencies: React, lucide-react.
 * Architecture: Reusable presentation component; action handling is delegated to callbacks.
 * Notes: Escape, backdrop, and Cancel close the modal.
 */
import { useEffect } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

/**
 * @contract ConfirmActionModalProps
 * Purpose: Configures a confirmation prompt and its host-owned action.
 * Fields: title, description, warning, confirmButtonLabel, variant, isSubmitting, onConfirm, onClose.
 * Rules: onConfirm may be asynchronous; confirmation is ignored while isSubmitting is true.
 * Sync: The host owns action completion and updates submission state.
 */
export interface ConfirmActionModalProps {
  /** Modal title, e.g. "Remove Phone Number?" */
  title: string;
  /** Description, e.g. "Are you sure you want to remove this phone number?" */
  description: React.ReactNode;
  /** Optional warning block */
  warning?: React.ReactNode;
  /** Text for the confirm button, default "Yes, Continue" */
  confirmButtonLabel?: string;
  /** Visual variant for confirm button and header */
  variant?: 'danger' | 'warning' | 'default';
  /** Whether the action is in progress */
  isSubmitting?: boolean;
  /** Called when user confirms */
  onConfirm: () => void | Promise<void>;
  /** Called when user cancels */
  onClose: () => void;
}

const VARIANT_STYLES = {
  danger: {
    headerBg: 'bg-red-50',
    headerIcon: 'text-red-600',
    headerTitle: 'text-red-900',
    closeBtn: 'text-red-400 hover:text-red-600',
    confirmBtn: 'bg-red-600 text-white hover:bg-red-700',
  },
  warning: {
    headerBg: 'bg-amber-50',
    headerIcon: 'text-amber-600',
    headerTitle: 'text-amber-900',
    closeBtn: 'text-amber-400 hover:text-amber-600',
    confirmBtn: 'bg-amber-600 text-white hover:bg-amber-700',
  },
  default: {
    headerBg: 'bg-gray-50',
    headerIcon: 'text-gray-500',
    headerTitle: 'text-gray-900',
    closeBtn: 'text-gray-400 hover:text-gray-600',
    confirmBtn: 'bg-gray-700 text-white hover:bg-gray-800',
  },
} as const;

/**
 * @component ConfirmActionModal
 * Purpose: Confirm a single host-defined action before it is performed.
 * Props: Title, description, optional warning/labels/variant/submission state, and callbacks.
 * Behavior: Supports danger, warning, and default styling; awaits confirmation and closes on cancel.
 * Data Source: local (display data and callbacks supplied by the host).
 * Constraints: The host owns the action and decides when the modal is mounted or dismissed.
 * Performance: Attaches one Escape-key listener while mounted and removes it on unmount.
 */
export default function ConfirmActionModal({
  title,
  description,
  warning,
  confirmButtonLabel = 'Yes, Continue',
  variant = 'danger',
  isSubmitting = false,
  onConfirm,
  onClose,
}: ConfirmActionModalProps) {
  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const styles = VARIANT_STYLES[variant];
  const HeaderIcon = variant === 'default' ? Info : AlertTriangle;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${styles.headerBg}`}>
          <HeaderIcon className={`h-5 w-5 shrink-0 ${styles.headerIcon}`} />
          <h3 className={`font-semibold ${styles.headerTitle}`}>{title}</h3>
          <button onClick={onClose} className={`ml-auto transition-colors ${styles.closeBtn}`}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-600">{description}</p>

          {/* Optional warning */}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>{warning}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-3 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${styles.confirmBtn}`}
            >
              {isSubmitting ? 'Processing...' : confirmButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
